import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import { api } from '../utils/api';
import { ThemeToggle } from '../components/ThemeToggle';
import { UsageGuide } from '../components/UsageGuide';
import { Check, Loader2, X } from 'lucide-react';

type SlugStatus =
  | { kind: 'idle' }
  | { kind: 'invalid'; reason: string }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'taken'; reason: string };

const SLUG_REGEX = /^[A-Za-z0-9][A-Za-z0-9_-]{2,31}$/;

function localValidate(slug: string): { valid: boolean; reason?: string } {
  if (!slug) return { valid: false, reason: 'Enter a name or leave blank for random' };
  if (slug.length < 3) return { valid: false, reason: 'At least 3 characters' };
  if (slug.length > 32) return { valid: false, reason: 'Max 32 characters' };
  if (!SLUG_REGEX.test(slug))
    return { valid: false, reason: 'Letters, numbers, _ and - only; must start with letter/number' };
  return { valid: true };
}

export function HomePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [slug, setSlug] = React.useState('');
  const [status, setStatus] = React.useState<SlugStatus>({ kind: 'idle' });

  React.useEffect(() => {
    const raw = slug.trim();
    if (!raw) {
      setStatus({ kind: 'idle' });
      return;
    }
    const local = localValidate(raw);
    if (!local.valid) {
      setStatus({ kind: 'invalid', reason: local.reason! });
      return;
    }
    setStatus({ kind: 'checking' });
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const result = await api.checkSlug(raw);
        if (cancelled) return;
        if (result.available) {
          setStatus({ kind: 'available' });
        } else {
          setStatus({ kind: 'taken', reason: result.reason || 'Already in use' });
        }
      } catch {
        if (!cancelled) setStatus({ kind: 'invalid', reason: 'Could not check name' });
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [slug]);

  const handleCreatePage = async () => {
    const raw = slug.trim();
    if (raw && status.kind !== 'available') return;
    setLoading(true);
    try {
      const page = await api.createPage(raw || undefined);
      navigate(`/${page.id}`);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to create clipboard page. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && (slug.trim() === '' || status.kind === 'available')) {
      handleCreatePage();
    }
  };

  const canSubmit =
    !loading && (slug.trim() === '' || status.kind === 'available');

  const buttonLabel = loading
    ? 'Creating…'
    : slug.trim()
    ? status.kind === 'available'
      ? `Create /${slug.trim()}`
      : 'Create'
    : 'Create Random Clipboard';

  return (
    <div className="min-h-screen bg-background">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="container mx-auto p-4 pt-10 pb-20 max-w-2xl">
        <Card className="mb-6">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold">Pastey</CardTitle>
            <CardDescription className="text-lg">
              Secure ephemeral clipboard service
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Create a temporary clipboard that automatically expires in 48 hours.
              Share up to 10 text snippets with unique URLs — from your browser, or straight from the terminal.
            </p>

            <div className="space-y-2">
              <label htmlFor="slug" className="text-sm font-medium">
                Choose a name (optional)
              </label>
              <div className="relative">
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="e.g. charlie — or leave blank for a random id"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  maxLength={32}
                  className="pr-10"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                  {status.kind === 'checking' && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  {status.kind === 'available' && (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                  {(status.kind === 'taken' || status.kind === 'invalid') && (
                    <X className="h-4 w-4 text-destructive" />
                  )}
                </div>
              </div>
              <div className="min-h-[1.25rem] text-xs">
                {status.kind === 'available' && (
                  <span className="text-green-600">
                    ✓ <code className="font-mono">pastey.link/{slug.trim()}</code> is free — claim it
                  </span>
                )}
                {status.kind === 'taken' && (
                  <span className="text-destructive">{status.reason}</span>
                )}
                {status.kind === 'invalid' && (
                  <span className="text-muted-foreground">{status.reason}</span>
                )}
                {status.kind === 'checking' && (
                  <span className="text-muted-foreground">Checking…</span>
                )}
                {status.kind === 'idle' && (
                  <span className="text-muted-foreground">
                    3-32 chars: letters, digits, <code>-</code>, <code>_</code>. Leave blank to get a random 5-char id.
                  </span>
                )}
              </div>
            </div>

            <Button
              onClick={handleCreatePage}
              disabled={!canSubmit}
              className="w-full"
              size="lg"
            >
              {buttonLabel}
            </Button>

            <div className="text-xs text-muted-foreground text-center space-y-1">
              <p>• Pages expire after 48 hours</p>
              <p>• Maximum 10 clips per page</p>
              <p>• Maximum 200KB per clip</p>
              <p>• Command-line access available — see below</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <UsageGuide pageId="<id>" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
